/**
 * Roles and permissions.
 *
 * Authorisation is enforced in two independent places, on purpose:
 *
 *   1. PostgreSQL Row Level Security — the backstop. Even a compromised or
 *      buggy API cannot read another organisation's ledger.
 *   2. This permission matrix — the fast, explainable, testable check that
 *      produces good error messages and drives what the UI renders.
 *
 * Layer 2 must never be the only check. If a permission exists here, a
 * corresponding RLS policy must exist in `supabase/migrations`. The access
 * tiers below are named after the SQL predicates they mirror
 * (`app.can_read_financials`, `app.can_write_financials`, …) so the two can be
 * compared side by side.
 *
 * WHAT THIS MATRIX DOES NOT EXPRESS
 *   Row-level narrowing. A technician holds `document:read`, but RLS restricts
 *   them to documents they uploaded. Table-level capability lives here;
 *   row-level scope lives in the policy. Do not try to encode the latter here.
 *
 * The role list is deliberately shaped around how an HVAC business actually
 * operates, including the external CPA who needs read access at year end
 * without being able to mutate anything.
 */
export const ORG_ROLES = [
    /** Business owner. Full control, including billing and destroying the org. */
    'owner',
    /** Trusted operator. Everything except billing and ownership transfer. */
    'admin',
    /** Day-to-day bookkeeping: categorise, correct, reconcile. */
    'bookkeeper',
    /** External CPA. Broad read access plus review/approval, no destructive rights. */
    'accountant',
    /** Field staff. Submits documents, sees only what they submitted. */
    'technician',
    /** Read-only observer. */
    'viewer',
];
export function isOrgRole(value) {
    return ORG_ROLES.includes(value);
}
/**
 * Seniority ranking, used only for guard rails such as "you cannot modify a
 * membership more senior than your own". It is NOT a permission hierarchy:
 * an accountant outranks a technician here but has a different, not larger,
 * permission set.
 */
export const ROLE_RANK = {
    owner: 100,
    admin: 80,
    bookkeeper: 60,
    accountant: 50,
    technician: 30,
    viewer: 10,
};
/** Every protected resource in the system. Append-only. */
export const RESOURCES = [
    // Tenancy
    'organization',
    'membership',
    'invitation',
    'audit_log',
    'storage_object',
    // Parties and operations
    'vendor',
    'customer',
    'property',
    'job',
    'equipment',
    // Evidence and the financial record
    'document',
    'transaction',
    'financial_account',
    // Tax and periods
    'tax_rule',
    'financial_period',
    // Intelligence
    'review_queue',
    'learning_rule',
    // Integration
    'export',
    'external_reference',
    // Collaboration
    'note',
    'task',
];
/** Every action that can be taken on a resource. Append-only. */
export const ACTIONS = ['create', 'read', 'update', 'delete', 'manage'];
export const permission = (resource, action) => `${resource}:${action}`;
/* -------------------------------------------------------------------------
 * Access tiers
 *
 * Named to mirror the SQL predicates in
 * supabase/migrations/*_row_level_security.sql. When one side changes, the
 * other must change with it, and having the same names on both sides is what
 * makes that check a two-minute diff rather than an audit.
 * ---------------------------------------------------------------------- */
/** Everyone with any membership. */
const EVERY_ROLE = ORG_ROLES;
/** `app.can_read_financials` — sees the books. */
const READ_FINANCIALS = [
    'owner',
    'admin',
    'bookkeeper',
    'accountant',
    'viewer',
];
/** `app.can_write_financials` — changes the books. */
const WRITE_FINANCIALS = ['owner', 'admin', 'bookkeeper'];
/** `app.can_manage_settings` — changes how the books work. */
const MANAGE_SETTINGS = ['owner', 'admin'];
/** `app.can_contribute` — may add evidence, including field staff. */
const CONTRIBUTE = ['owner', 'admin', 'bookkeeper', 'technician'];
/**
 * `app.can_collaborate` — everyone except the viewer.
 *
 * A viewer is a read-only observer: a lender, a silent partner, an auditor
 * mid-engagement. Letting them annotate would break the one property that
 * makes the role safe to hand out, namely that it writes nothing at all.
 */
const COLLABORATE = [
    'owner',
    'admin',
    'bookkeeper',
    'accountant',
    'technician',
];
/**
 * The authoritative access table: which roles may take which action on which
 * resource.
 *
 * An explicit table rather than a computed hierarchy. Inheritance chains
 * quietly grant things nobody intended, which is exactly the failure mode a
 * financial system cannot have. Absent action = nobody, which is why deletion
 * is missing from most rows: financial records are voided, never removed.
 */
const ACCESS = {
    /* --- Tenancy --------------------------------------------------------- */
    organization: {
        read: EVERY_ROLE,
        update: MANAGE_SETTINGS,
        delete: ['owner'],
        manage: ['owner'],
    },
    membership: {
        create: MANAGE_SETTINGS,
        read: ['owner', 'admin', 'bookkeeper', 'accountant'],
        update: MANAGE_SETTINGS,
        delete: MANAGE_SETTINGS,
    },
    invitation: {
        create: MANAGE_SETTINGS,
        read: MANAGE_SETTINGS,
        delete: MANAGE_SETTINGS,
    },
    audit_log: {
        read: ['owner', 'admin', 'bookkeeper', 'accountant'],
    },
    storage_object: {
        create: CONTRIBUTE,
        read: EVERY_ROLE,
        delete: MANAGE_SETTINGS,
    },
    /* --- Parties and operations ------------------------------------------ */
    vendor: {
        create: WRITE_FINANCIALS,
        read: EVERY_ROLE,
        update: WRITE_FINANCIALS,
    },
    customer: {
        create: WRITE_FINANCIALS,
        read: EVERY_ROLE,
        update: WRITE_FINANCIALS,
    },
    property: {
        create: WRITE_FINANCIALS,
        read: EVERY_ROLE,
        update: WRITE_FINANCIALS,
    },
    job: {
        create: WRITE_FINANCIALS,
        read: EVERY_ROLE,
        update: WRITE_FINANCIALS,
    },
    equipment: {
        // Field staff record what they installed.
        create: CONTRIBUTE,
        read: EVERY_ROLE,
        update: CONTRIBUTE,
    },
    /* --- Evidence and the financial record -------------------------------- */
    document: {
        // A technician may submit; RLS narrows their reads to their own uploads.
        create: CONTRIBUTE,
        read: [...READ_FINANCIALS, 'technician'],
        update: WRITE_FINANCIALS,
    },
    transaction: {
        create: WRITE_FINANCIALS,
        read: READ_FINANCIALS,
        update: WRITE_FINANCIALS,
        // No delete, at any role. A transaction is voided, never removed.
    },
    financial_account: {
        create: MANAGE_SETTINGS,
        read: READ_FINANCIALS,
        update: MANAGE_SETTINGS,
    },
    /* --- Tax and periods --------------------------------------------------- */
    tax_rule: {
        create: MANAGE_SETTINGS,
        read: READ_FINANCIALS,
        update: MANAGE_SETTINGS,
    },
    financial_period: {
        create: MANAGE_SETTINGS,
        read: READ_FINANCIALS,
        update: MANAGE_SETTINGS,
        manage: MANAGE_SETTINGS,
    },
    /* --- Intelligence ------------------------------------------------------ */
    review_queue: {
        // The accountant reviews; that is the point of the role.
        read: READ_FINANCIALS,
        update: ['owner', 'admin', 'bookkeeper', 'accountant'],
    },
    learning_rule: {
        create: WRITE_FINANCIALS,
        read: READ_FINANCIALS,
        update: WRITE_FINANCIALS,
    },
    /* --- Integration ------------------------------------------------------- */
    export: {
        create: WRITE_FINANCIALS,
        read: READ_FINANCIALS,
        update: WRITE_FINANCIALS,
    },
    external_reference: {
        create: WRITE_FINANCIALS,
        read: EVERY_ROLE,
        update: WRITE_FINANCIALS,
        delete: MANAGE_SETTINGS,
    },
    /* --- Collaboration ----------------------------------------------------- */
    note: {
        create: COLLABORATE,
        read: EVERY_ROLE,
        // RLS additionally restricts editing to the author or an admin, and hides
        // internal notes from the external accountant.
        update: COLLABORATE,
    },
    task: {
        create: COLLABORATE,
        read: EVERY_ROLE,
        update: COLLABORATE,
    },
};
/**
 * Role → permission list, derived from the access table.
 *
 * Derivation rather than a second hand-written table: two hand-written views
 * of the same facts drift, and the transposed one is the one nobody rereads.
 */
export const ROLE_PERMISSIONS = Object.freeze(Object.fromEntries(ORG_ROLES.map((role) => [
    role,
    Object.freeze(RESOURCES.flatMap((resource) => ACTIONS.filter((action) => ACCESS[resource][action]?.includes(role) ?? false).map((action) => permission(resource, action)))),
])));
/** Whether a single role grants a permission. */
export function roleHasPermission(role, required) {
    return ROLE_PERMISSIONS[role].includes(required);
}
/**
 * Whether any of the caller's roles grants the permission.
 *
 * A user may hold exactly one role per organisation today, but the signature
 * takes a list so that adding a second (e.g. a project-scoped role) later does
 * not require touching every call site.
 */
export function hasPermission(roles, required) {
    return roles.some((role) => roleHasPermission(role, required));
}
/** All permissions held, deduplicated. Used to hydrate the frontend session. */
export function permissionsForRoles(roles) {
    return [...new Set(roles.flatMap((role) => ROLE_PERMISSIONS[role]))].sort();
}
/** Guard rail: an actor may only manage memberships strictly below their rank. */
export function canManageRole(actorRole, targetRole) {
    return ROLE_RANK[actorRole] > ROLE_RANK[targetRole];
}
/** Every role permitted to take an action on a resource. For tests and docs. */
export function rolesWithAccess(resource, action) {
    return ACCESS[resource][action] ?? [];
}
//# sourceMappingURL=roles.js.map